// Plano de mesas del restaurante (módulo ventas_restaurante): lógica pura, sin Vue ni store.
// El servidor guarda posX/posY en UNIDADES LÓGICAS de un plano fijo (`canvas.width` ×
// `canvas.height`, p.ej. 1200×700) y manda el tamaño de cada forma en `canvas.sizes`; el
// cliente solo escala a su ancho. Así la misma distribución se ve igual en monitor y tablet.

export const NO_ZONE = 'none';   // pestaña virtual para mesas sin zona
export const ALL_ZONES = 'all';  // pestaña «Todas»: todas las zonas a la vez, una debajo de otra
export const SNAP = 20;          // grilla de arrastre, en unidades lógicas
const CROP_PADDING = 40;         // aire alrededor de las mesas al recortar un plano
const CROP_MIN_HEIGHT = 200;

const DEFAULT_CANVAS = {
    width: 1200,
    height: 700,
    sizes: { square: { width: 120, height: 120 }, round: { width: 120, height: 120 },
             rect: { width: 200, height: 120 } },
};

export function canvasOf(floor) {
    return (floor && floor.canvas) || DEFAULT_CANVAS;
}

export function tableSize(canvas, shape) {
    const sizes = (canvas && canvas.sizes) || DEFAULT_CANVAS.sizes;
    return sizes[shape] || sizes.square;
}

export function snap(value, step = SNAP) {
    return Math.round(value / step) * step;
}

// La mesa entera queda dentro del plano (mismo criterio que el servidor).
export function clampPosition(canvas, shape, posX, posY) {
    const { width, height } = tableSize(canvas, shape);
    const clamp = (v, max) => Math.max(0, Math.min(max, v));
    return { posX: clamp(Math.round(posX), canvas.width - width),
             posY: clamp(Math.round(posY), canvas.height - height) };
}

// Píxeles que se movió el puntero → unidades lógicas, según el ancho real del plano en pantalla.
export function pxToUnits(deltaPx, planeWidthPx, canvasWidth) {
    if (!planeWidthPx) return 0;
    return deltaPx * (canvasWidth / planeWidthPx);
}

// Posición final de un arrastre: origen + delta, ajustada a la grilla y dentro del plano.
export function dragTarget(canvas, table, deltaXPx, deltaYPx, planeWidthPx) {
    const scale = (d) => pxToUnits(d, planeWidthPx, canvas.width);
    return clampPosition(canvas, table.shape,
                         snap((table.posX || 0) + scale(deltaXPx)),
                         snap((table.posY || 0) + scale(deltaYPx)));
}

// Franja vertical del plano que se dibuja: el plano entero, o (vista «Todas») solo la franja donde
// hay mesas. Se recorta SOLO en alto: el ancho sigue completo para que una mesa mida lo mismo en
// todas las zonas apiladas.
export function cropView(canvas, tables) {
    if (!tables || !tables.length) return { y: 0, height: canvas.height };
    let top = Infinity;
    let bottom = -Infinity;
    for (const t of tables) {
        const y = t.posY || 0;
        top = Math.min(top, y);
        bottom = Math.max(bottom, y + tableSize(canvas, t.shape).height);
    }
    const y = Math.max(0, top - CROP_PADDING);
    let end = Math.min(canvas.height, bottom + CROP_PADDING);
    if (end - y < CROP_MIN_HEIGHT) end = Math.min(canvas.height, y + CROP_MIN_HEIGHT);
    return { y, height: end - y };
}

// Caja de la mesa en % de la franja visible del plano (para `position: absolute` responsivo).
export function percentBox(canvas, table, view = { y: 0, height: canvas.height }) {
    const { width, height } = tableSize(canvas, table.shape);
    return {
        left: ((table.posX || 0) / canvas.width) * 100,
        top: (((table.posY || 0) - view.y) / view.height) * 100,
        width: (width / canvas.width) * 100,
        height: (height / view.height) * 100,
    };
}

// Cambios de posición sin guardar (modo «Configurar salón») encima de lo que vino del servidor.
// Si llega un push en vivo mientras el dueño arrastra, el salón se recarga pero su trabajo no se pierde.
export function applyPending(tables, pending) {
    if (!pending || !Object.keys(pending).length) return tables;
    return tables.map(t => (pending[t.id] ? { ...t, ...pending[t.id] } : t));
}

// Quita del pendiente las mesas que ya no existen (otro admin las borró).
export function prunePending(pending, tables) {
    const ids = new Set(tables.map(t => String(t.id)));
    const out = {};
    for (const [id, pos] of Object.entries(pending || {})) {
        if (ids.has(String(id))) out[id] = pos;
    }
    return out;
}

export function tablesForZone(tables, zoneId) {
    if (zoneId === NO_ZONE) return tables.filter(t => !t.zoneId);
    return tables.filter(t => t.zoneId === zoneId);
}

// Pestañas del salón: las zonas en su orden + «Sin zona» solo si hay mesas sueltas.
export function zoneTabs(zones, tables) {
    const tabs = (zones || []).map(z => ({ id: z.id, name: z.name,
                                           tableCount: tables.filter(t => t.zoneId === z.id).length }));
    const loose = tables.filter(t => !t.zoneId).length;
    if (loose) tabs.push({ id: NO_ZONE, name: 'Sin zona', tableCount: loose });
    return tabs;
}

// Lo que se dibuja según la pestaña: una zona, o (Todas) cada zona como sección propia. Fuera del
// modo edición, «Todas» omite las zonas vacías (no aportan nada al mirar el salón).
export function floorSections(tabs, tables, selectedId, { includeEmpty = false } = {}) {
    if (selectedId !== ALL_ZONES) {
        const tab = tabs.find(z => z.id === selectedId);
        return tab ? [{ id: tab.id, name: tab.name, tables: tablesForZone(tables, tab.id) }] : [];
    }
    return tabs
        .map(z => ({ id: z.id, name: z.name, tables: tablesForZone(tables, z.id) }))
        .filter(s => includeEmpty || s.tables.length);
}

// Nuevo orden de zonas al subir/bajar una: devuelve [{id, order}] solo de las que cambian.
export function reorderZones(zones, zoneId, direction) {
    const list = [...zones];
    const index = list.findIndex(z => z.id === zoneId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= list.length) return [];
    [list[index], list[target]] = [list[target], list[index]];
    return list.map((z, order) => ({ id: z.id, order, changed: z.order !== order }))
               .filter(z => z.changed)
               .map(({ id, order }) => ({ id, order }));
}
